import { useMutation } from '@tanstack/react-query';
import { triviaStore, useTriviaStore } from '../stores/trivia-store';
import { useInvalidateCurrentUser } from './user-info';
import { useHandleCommonError } from '@wwe-portal/web/utils/error-handler/error';
import {
  useInvalidateTriviaLeaderboard,
  useTriviaRumbleCost,
} from './trivia-rumble';
import {
  sendTriviaGameContinueEvent,
  sendTriviaGamePlayAgainEvent,
  sendTriviaGameStartEvent,
  sendTriviaGameWonEvent,
} from '@wwe-portal/web/libs/analytics/trivia-rumble';

export const useSubmitAnswerMutation = () => {
  const { data: triviaCost } = useTriviaRumbleCost();
  const reward = triviaCost?.data.data.reward;
  const choseAnswer = useTriviaStore((state) => state.choseAnswer);
  const closeGame = useTriviaStore((state) => state.closeGame);
  const handleCommonError = useHandleCommonError();
  const invalidate = useInvalidateTriviaLeaderboard();
  const invalidateUserInfo = useInvalidateCurrentUser();

  return useMutation({
    mutationFn: choseAnswer,
    onError: (error) => {
      handleCommonError(error);
      closeGame();
    },
    onSuccess: async () => {
      await Promise.all([invalidate(), invalidateUserInfo()]);

      const currentUser = await invalidateUserInfo();
      const gameStatus = triviaStore.getState().gameStatus;
      // Send game victory analytics event
      if (currentUser && gameStatus === 'won') {
        sendTriviaGameWonEvent({
          currentUser,
          reward,
        });
      }
    },
  });
};

export const useTriviaContinueMutation = () => {
  const continueAnswer = useTriviaStore((state) => state.continueAnswer);
  const closeGame = useTriviaStore((state) => state.closeGame);
  const invalidateUserInfo = useInvalidateCurrentUser();
  const handleCommonError = useHandleCommonError();
  return useMutation({
    mutationFn: async () => {
      const gameData = triviaStore.getState().gameData;
      const questionStep = gameData?.currentQuestion.id;
      const neededWebCoin = gameData?.retryCost;
      await continueAnswer();
      const currentUser = await invalidateUserInfo();

      // Send game continue analytics
      if (currentUser) {
        sendTriviaGameContinueEvent({
          currentUser,
          questionStep,
          neededWebCoin,
        });
      }
    },
    onError: (error) => {
      handleCommonError(error);
      closeGame();
    },
  });
};

export const usePlayAgainMutation = () => {
  const playAgain = useTriviaStore((state) => state.playAgain);
  const closeGame = useTriviaStore((state) => state.closeGame);
  const invalidateUserInfo = useInvalidateCurrentUser();
  const handleCommonError = useHandleCommonError();
  const { data: triviaCost } = useTriviaRumbleCost();
  return useMutation({
    mutationFn: async () => {
      await playAgain();
      const currentUser = await invalidateUserInfo();

      if (currentUser) {
        sendTriviaGamePlayAgainEvent({
          currentUser,
          energyCost: triviaCost?.data.data.energyCost,
        });
      }
    },
    onError: (error) => {
      handleCommonError(error);
      closeGame();
    },
  });
};

export const useStartGameMutation = () => {
  const startGame = useTriviaStore((state) => state.startGame);
  const closeGame = useTriviaStore((state) => state.closeGame);
  const invalidateUserInfo = useInvalidateCurrentUser();
  const handleCommonError = useHandleCommonError();
  const { data: triviaCost } = useTriviaRumbleCost();

  return useMutation({
    mutationFn: async () => {
      await startGame();
      const currentUser = await invalidateUserInfo();

      if (currentUser) {
        sendTriviaGameStartEvent({
          currentUser,
          energyCost: triviaCost?.data.data.energyCost,
        });
      }
    },
    onError: (error) => {
      handleCommonError(error);
      closeGame();
    },
    // Add retry logic for better reliability
    retry: 2,
  });
};
